import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Divider,
  LinearProgress,
  Switch,
  FormControlLabel,
  Stack,
  IconButton,
  Collapse,
  useMediaQuery,
  useTheme,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import api from "../../../../services/api";
import SupplierGroup from "./SupplierGroup";
import ShoppingListItemCard from "./ShoppingListItemCard";
import { useSnackbarAlert } from "../../../../hooks/useSnackbarAlert";
import { formatDateInNewfoundland } from "../../../../utils/dates/formatDateInNewfoundland";
import { useAuth } from "../../../../context/AuthContext";

const DriverShoppingList = () => {
  const { id: listId } = useParams();
  const navigate = useNavigate();
  const snackbar = useSnackbarAlert();
  const { user } = useAuth();

  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBought, setShowBought] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [showMeta, setShowMeta] = useState(!isMobile);

  useEffect(() => {
    const fetchList = async () => {
      try {
        const res = await api.get(`/admin/shopping-lists/${listId}`);
        setList(res.data);
      } catch (err) {
        console.error("Failed to fetch shopping list:", err);
        snackbar.error("Failed to load list.");
      } finally {
        setLoading(false);
      }
    };
    if (listId) fetchList();
  }, [listId]);

  const updateItemStatus = async (_id, statusUpdate) => {
    const updatedItems = list.items.map((item) =>
      item._id === _id ? { ...item, ...statusUpdate } : item
    );
    setList({ ...list, items: updatedItems });

    try {
      await api.patch(
        `/admin/shopping-lists/${list._id}/item/${_id}`,
        statusUpdate
      );
      snackbar.success("Item updated.");
    } catch (err) {
      console.error("Failed to save status:", err);
      snackbar.error("Could not save update.");
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await api.delete(`/admin/shopping-lists/${list._id}/item/${itemId}`);
      setList((prev) => ({
        ...prev,
        items: prev.items.filter((item) => item._id !== itemId),
      }));
      snackbar.success("Item removed.");
    } catch (err) {
      console.error("Failed to delete item:", err);
      snackbar.error("Could not delete item.");
    }
  };

  if (loading) return <CircularProgress />;
  if (!list) return <Typography color="error">List not found.</Typography>;

  const visibleItems = list.items.filter(
    (i) => showBought || i.status !== "bought"
  );

  // ✅ Group by supplier name (excluding null or empty)
  const groupedBySupplier = visibleItems.reduce((acc, item) => {
    const supplier = item.store?.trim();
    if (!supplier) return acc; // ❌ Don't show "Unassigned Section"
    if (!acc[supplier]) acc[supplier] = [];
    acc[supplier].push(item);
    return acc;
  }, {});

  const completed = list.items.filter((i) => i.status === "bought").length;

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        mb={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton
            size="small"
            onClick={() => navigate("/admin/driver-checklist")}
            sx={{ p: 0.5 }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle1" noWrap>
            {list.storeId.displayName}
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1}>
          {(user?.employee?.role === "admin" ||
            user?.employee?.role === "manager") && (
            <Tooltip title="Edit List">
              <IconButton
                size="small"
                onClick={() =>
                  navigate(`/admin/shopping-lists/edit/${list._id}`)
                }
                sx={{
                  p: 0.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Show/Hide Details">
            <IconButton
              size="small"
              onClick={() => setShowMeta((prev) => !prev)}
              sx={{ p: 0.5 }}>
              {showMeta ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Collapse in={showMeta}>
        <Typography variant="caption" gutterBottom>
          {formatDateInNewfoundland(list.shoppingDate, "MMMM D, YYYY")} @{" "}
          {formatDateInNewfoundland(list.createdAt, "h:mm A")}
        </Typography>

        <Typography fontSize={13} sx={{ my: 1 }}>
          {completed} / {list.items.length} items marked as bought
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={showBought}
              onChange={(e) => setShowBought(e.target.checked)}
            />
          }
          label="Show Bought Items"
          sx={{ mb: 1 }}
        />

        <LinearProgress
          variant="determinate"
          value={(completed / list.items.length) * 100}
          sx={{ mb: 1 }}
        />
      </Collapse>

      <Divider sx={{ my: 2 }} />

      {/* ✅ Render each supplier group */}
      {Object.entries(groupedBySupplier).map(([supplier, items]) => {
        if (supplier === "Costco") {
          return (
            <SupplierGroup
              key="Costco"
              supplier="Costco"
              items={[...items].sort(
                (a, b) =>
                  (a.supplyItem?.storePathIndex ?? 999) -
                  (b.supplyItem?.storePathIndex ?? 999)
              )}
              onStatusChange={updateItemStatus}
              onDelete={handleDeleteItem}
              defaultOpen={true}
            />
          );
        }

        return (
          <Box key={supplier} sx={{ mt: 2 }}>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              sx={{ mb: 1, pl: 0.5 }}>
              {supplier}
            </Typography>
            {items.map((item) => (
              <ShoppingListItemCard
                key={item._id}
                item={item}
                onStatusChange={(update) => updateItemStatus(item._id, update)}
                onDelete={handleDeleteItem}
              />
            ))}
          </Box>
        );
      })}
    </Box>
  );
};

export default DriverShoppingList;
